<template>
  <div class="p-b-3">
    <job-request-pane>
      <template slot="title">
        You're almost done!
      </template>
      <template slot="subtitle">
        Your privacy is our top priority. This information is required so pros can contact you.
      </template>
    </job-request-pane>

    <div class="container p-x-2:mo m-b-6">
      <div class="m-b-6">
        <div class="layout gap-4:t">
          <div class="layout-item is-12 is-7:t is-8:d">
            <request-form
                :validation="validation"
                :disabled="submitting"
                :user-exists="userExists"
                @sign-in-clicked="handleLoginClick"/>
          </div>
        </div>
      </div>

      <preferred-contact class="text-bold" v-model="preferredContactMethod" :disabled="submitting"/>

      <div class="m-t-2 OPTINSECTION">
        <div class="container checkboxharmony m-t-2 m-b-2">
          <input
              id="remember"
              type="checkbox"
              v-model="checked">
          <label selenium="sms-checkbox" class= "m-l-2 text-bold" for="remember">
            Get SMS updates about your service request
          </label>
          <p selenium="sms-text" class="p-2">
            Pro Referral may send you
            text messages about this service request.
            Message & data rates may apply. Texts message
            frequency varies and may be sent between 8:00
            am & 9:00 pm. Text STOP to cancel or HELP for
            help. For questions, contact 1-855-723-2266.
            <a href="https://www.proreferral.com/about/legal/privacy" class="c-depot">Privacy& Security Statement</a>
          </p>
        </div>
      </div>
    </div>

    <div class="container m-b-6">
      <job-summary/>
    </div>

    <div class="container p-x-2:mo">
      <div class="m-b-2">
        By clicking "Submit My Request" below, I confirm acceptance of Pro Referral's
        <span class="link" selenium="tos-link" @click="showTOS = true">Terms of Service.</span>
      </div>

      <!-- TOS MODAL -->
      <tos-modal v-if="showTOS" @close="showTOS = false"/>
      <!-- LOGIN MODAL -->
      <login-modal
          v-if="showLoginModal"
          :initial-email="email"
          :auto-redirect="false"
          show-welcome-message
          @close="closeLoginModal"
          @login-success="closeLoginModal"/>
      <!-- RECAPCHA -->
      <!-- TODO(adam): this causes 33ms layout thrash for some reason -->
      <vue-recaptcha
          ref="invisibleRecaptcha"
          :sitekey="$runtimeConstants.RECAPTCHA_KEY"
          size="invisible"
          @verify="verify"
          @expired="captchaExpired"/>
      <leads-modal
          v-if="showLimitModal"
          @close="showLimitModal = false"/>

      <div
          v-if="validation.hasError()"
          error="Please correct all errors before continuing."
          class="input-container input-message error m-b-2"/>

      <button
          :disabled="disabled"
          class="btn btn-large w-100:mo"
          selenium="submit-my-request-button"
          @click="captchaExecute">
        {{ submitting ? 'Please wait...' : 'Submit My Request' }}
      </button>
    </div>
  </div>
</template>

<script>
import SimpleVueValidator, { Validator } from 'simple-vue-validator';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import {
  getContacts,
  validateLogin,
} from '@homeservices/api-kit/security/users';
import { mapGetters } from 'vuex';
import VueRecaptcha from 'vue-recaptcha';
import axios, { CancelToken, isCancel } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Storage from '@homeservices/tool-kit/storage/local-storage';
import { passwordValidator } from '@/utils/password-validator';
import { JOB_REQUEST_LOCAL_STORAGE_NAME } from '@/store/modules/jobrequest';
import JobRequestPane from '@/views/JobRequest/components/JobRequestPane';
import LoginModal from '@/components/LoginModal';
import RequestForm from './components/RequestForm';
import SignIn from './components/SignIn';
import PreferredContact from './components/PreferredContact';
import JobSummary from './components/JobSummary';
import TosModal from './components/TosModal';
import LeadsModal from './components/LeadsModal';

// TODO(adam): this affects the entire app
SimpleVueValidator.setMode('conservative');

export default {
  name: 'job-overview',
  components: {
    VueRecaptcha,
    JobRequestPane,
    RequestForm,
    SignIn,
    PreferredContact,
    JobSummary,
    TosModal,
    LoginModal,
    LeadsModal,
  },
  mixins: [
    SimpleVueValidator.mixin,
  ],
  data () {
    return {
      showTOS: false,
      submitting: false,
      userExists: false,
      validateLoginSource: null,
      showLoginModal: false,
      showLimitModal: false,
      sessionId: null,
    };
  },
  computed: {
    ...mapGetters('user', [
      'isLoggedIn',
      'me',
    ]),
    ...mapGetters('jobrequest', [
      'sourceID',
    ]),
    ...mapGetters('jobrequest/details', [
      'selectedUrgencyId',
      'notes',
      'uploadedImages',
    ]),
    ...mapGetters('jobrequest/overview', [
      'email',
      'password',
      'passwordConfirmation',
      'firstName',
      'lastName',
      'phone',
      'tosId',
    ]),
    ...mapGetters('riskAssesment', [
      'isFeatureEnabled',
      'pilottedSteps',
      'rcpScore',
    ]),
    ...mapGetters('recaptcha', [
      'isRecaptchaAvailable',
      'getRiskResult',
      'getOverallRiskResult',
      'userInteractionId',
    ]),
    preferredContactMethod: {
      get () {
        return this.$store.getters['jobrequest/overview/preferredContactMethod'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setPreferredContactMethod', value);
      },
    },
    checked: {
      get () {
        return this.$store.getters['jobrequest/overview/smsOptIn'];
      },
      set (value) {
        this.$store.dispatch('jobrequest/overview/setSmsOptIn', value);
      },
    },
    shouldCaptureRecaptchaScore () {
      return Boolean(this.rcpScore);
    },
    isServiceConnectRedirected () {
      return Boolean(this.$route.query.lead);
    },
    disabled () {
      return this.submitting || (!this.isLoggedIn && this.userExists);
    },
  },
  validators: {
    email (value) {
      return Validator.value(value)
        .required('Please enter your email address.')
        .email('Please enter a valid email address.');
    },
    password (value) {
      if (!this.isLoggedIn) {
        return passwordValidator(value);
      }
    },
    passwordConfirmation (value) {
      if (!this.isLoggedIn) {
        return Validator.value(value).required('Please confirm your password');
      }
    },
    firstName (value) {
      return Validator.value(value).required('Please enter your first name.');
    },
    lastName (value) {
      return Validator.value(value).required('Please enter your last name.');
    },
    phone (value) {
      return Validator.value(value)
        .required('Please enter your phone number.')
        .length(10, 'Please enter a valid 10-digit phone number.');
    },
  },
  watch: {
    email: {
      handler: _debounce(function checkIfUserExists (value) {
        this.userExists = false;

        // this should really be checking the email field validation is in a valid state
        // but we can't do that because of simple-vue-validator's limitations. for now we have
        // to just copy the "email" regex and use it here.
        // eslint-disable-next-line no-useless-escape
        if (!value || !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value)) {
          return;
        }

        if (this.validateLoginSource) {
          this.validateLoginSource.cancel();
        }

        this.validateLoginSource = CancelToken.source();

        return validateLogin(this.email, this.validateLoginSource.token).catch((e) => {
          if (isCancel(e)) {
            return;
          }

          this.userExists = true;
          if (!this.isLoggedIn) {
            this.openLoginModal();
          }
        });
      }, 500),
      immediate: true,
    },
    me: {
      async handler () {
        if (!this.me.id) {
          return;
        }
        try {
          const { data: contacts } = await getContacts({
            user_ids: this.me.id,
            contact_types: 'PHONE,EMAIL',
          });
          const phone = _get(contacts.results.find(c => c.contact_type === 'PHONE'), 'contact');
          const email = _get(contacts.results.find(c => c.contact_type === 'EMAIL'), 'contact');

          this.$store.dispatch('jobrequest/overview/setPhone', phone);
          this.$store.dispatch('jobrequest/overview/setEmail', email);
          this.$store.dispatch('jobrequest/overview/setFirstName', this.me.first_name);
          this.$store.dispatch('jobrequest/overview/setLastName', this.me.last_name);
        } catch (err) {
          console.error(err);
          this.$store.dispatch('user/logoutAndClearUserSession');
        }
      },
      immediate: true,
    },
  },
  async mounted () {
    try {
      this.$store.dispatch('jobrequest/overview/fetchTosId');
      const selectedService = this.$store.state.jobrequest && this.$store.state.jobrequest.service;
      const serviceIdentifier = selectedService && selectedService.service_identifier;
      await this.$store.dispatch('riskAssesment/fetchRiskAssessmentConfig', {
        app_name: 'proreferral',
        sp_flow: 'the_home_depot_proreferral',
        service_identifier: serviceIdentifier || this.$route.params.serviceId,
      });
    } catch (e) {
      console.error('[Mounted] Error fetching risk assessment config:', e); // eslint-disable-line no-console
    }
    if (!this.sessionId) {
      this.sessionId = uuidv4();
    }
    if (!window.referral || !window.referral.profile) {
      const script = document.createElement('script');
      script.src = '/libs/pro-tool-v5.js';
      script.onload = () => {
        this.profileTMX();
      };
      document.head.appendChild(script);
    } else {
      this.profileTMX();
    }
  },
  methods: {
    openLoginModal () {
      this.showLoginModal = true;
    },
    closeLoginModal () {
      this.showLoginModal = false;
    },
    handleLoginClick () {
      this.$router.push({
        name: 'login',
        query: {
          redirect: this.$route.fullPath,
        },
      });
    },
    async submit (recaptchaResponse) {
      this.submitting = true;
      try {
        if (!this.isLoggedIn) {
          await this.setUpUser();
        } else {
          await this.updateUserTos();
        }

        // if the user uses adblocking then the window.scamPhoneNumbers will be undefined
        // so we have to check for it. obviously, this is not great spam protection here.
        const { data } = await axios.get('/api/contentful/phonelist');
        const gtmList = window.scamPhoneNumbers || [];
        if (!gtmList.length) {
          this.$events.$emit(this.$events.POSSIBLE_AD_BLOCKER, {
            scamNumber: this.phone,
            source: 'GTM',
          });
        }
        const scamPhoneNumbers = data.list || [];
        if (!scamPhoneNumbers.length) {
          this.$events.$emit(this.$events.POSSIBLE_AD_BLOCKER, {
            scamNumber: this.phone,
            source: 'contentful',
          });
        }
        const isScam = scamPhoneNumbers.find(number => number === this.phone) !== undefined;

        if (isScam) {
          this.$events.$emit(this.$events.REQUEST_JOBSUBMIT_SCAM, {
            scamNumber: this.phone,
          });
          this.$router.push({
            name: 'jobrequest::confirmation',
            params: {
              zipcode: this.$route.params.zipcode,
              serviceId: this.$route.params.serviceId,
            },
          });
          return;
        }

        await this.captureRecaptchaScore('lead', recaptchaResponse);
        // Check if both action and res are false (can be boolean false or string "false")
        const isActionFalse = this.getRiskResult === true || this.getRiskResult === 'true';
        const isResFalse = this.getOverallRiskResult === true || this.getOverallRiskResult === 'true';
        if (isActionFalse && isResFalse) {
          this.showLimitModal = true;
          this.submitting = false;
          this.$events.$emit(this.$events.LIMIT_MODAL_OPEN);
          return;
        }

        const params = {
          zipcode: this.$route.params.zipcode,
          recaptchaResponse,
          leadHash: this.$route.query.lead,
          sms: this.checked,
        };


        // `lead` in the query parameters is `leadHash` not the
        // same thing as leadId we recieve back from workorder API
        // after we submit the request
        const { data: { user_friendly_identifier: leadId } } = await this.$store.dispatch('jobrequest/submit', params);

        this.$events.$emit(this.$events.REQUEST_JOBSUBMIT_SUCCESS, {
          zipcode: this.$route.params.zipcode,
          serviceId: this.$route.params.serviceId,
          leadId,
          sourceID: this.sourceID,
        });
        Storage.remove(JOB_REQUEST_LOCAL_STORAGE_NAME);
        this.$router.push({
          name: 'jobrequest::confirmation',
          params: {
            zipcode: this.$route.params.zipcode,
            serviceId: this.$route.params.serviceId,
            leadId,
          },
        });
      } catch (error) {
        this.submitting = false;
        this.$refs.invisibleRecaptcha.reset();
        this.$events.$emit(this.$events.REQUEST_JOBSUBMIT_ERROR, {
          ...error.response,
          zipcode: this.$route.params.zipcode,
          serviceId: this.$route.params.serviceId,
        });
        throw (error);
      }
    },
    async verify (token) {
      try {
        await axios.post('/recaptcha/verify', {
          secret: this.$runtimeConstants.RECAPTCHA_KEY,
          token,
          distinct_id: this.me.id,
        });
        this.submit(token);
        this.updateTMX();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    captchaExpired () {
      this.$refs.invisibleRecaptcha.reset();
    },
    captchaExecute () {
      this.$validate()
        .then(async (valid) => {
          if (!this.isLoggedIn && this.userExists) {
            // if user exists but they are not logged in, don't continue to submit
            return;
          }

          if (valid) {
            this.$refs.invisibleRecaptcha.execute();
          } else {
            const errors = {};
            this.$refs.requestForm.validation.errors.forEach((error) => {
              const { field, message } = error;
              errors[`error ${field}`] = message;
            });
            this.$events.$emit(this.$events.REQUEST_VALIDATION_ERROR, {
              ...errors,
            });
          }
        });
    },
    async captureRecaptchaScore (stepTrackingName, recaptchaToken) {
      if (
        this.shouldCaptureRecaptchaScore
        && this.pilottedSteps && this.pilottedSteps.includes('lead')
      ) {
        await this.getRecaptchaScoreForActiveStep(stepTrackingName || 'lead', recaptchaToken);
      }
    },
    async getRecaptchaScoreForActiveStep (actionname = 'lead', recaptchaToken = null) {
      if (!this.isFeatureEnabled) {
        return;
      }

      this.$store.commit('recaptcha/updateIsRecaptchaLoading', true);
      let recaptchaSysId = null;
      try {
        recaptchaSysId = `grc_${uuidv4()}`;
        await this.$store.dispatch('recaptcha/getRecaptchaScore', {
          action: actionname,
          recaptchaSysId,
          recaptchaToken,
        });

        if (this.isRecaptchaAvailable) {
          this.$events.$emit(this.$events.RECAPTCHA_TOKEN_FETCHED, {
            pageTitle: document.title,
            testLead: this.isServiceConnectRedirected,
            serviceId: this.$route.params.serviceId,
            flowName: 'the_home_depot_proreferral',
            riskAssessment: this.getRiskResult,
            stepName: actionname,
            userInteractionId: this.userInteractionId,
            recaptchaSessionId: recaptchaSysId,
          });
        }
      } catch (e) {
        console.error('✗ Error in getRecaptchaScoreForActiveStep:', e);
        console.error('Error message:', e.message, 'Stack:', e.stack);
        this.$emit('error', e);
      } finally {
        this.$store.commit('recaptcha/updateIsRecaptchaLoading', false);
      }
    },
    async setUpUser () {
      const userData = {
        first_name: this.firstName,
        last_name: this.lastName,
        consumer_terms_of_service_id: this.tosId,
        default_contact_type: this.preferredContactMethod,
        phone: this.phone,
        email: this.email,
        password: this.password,
      };

      await this.$store.dispatch('user/create', userData);
      this.$events.$emit(this.$events.USER_CREATION, { id: this.me.id });
    },
    async updateUserTos () {
      const userData = {
        consumer_terms_of_service_id: this.tosId,
      };

      await this.$store.dispatch('user/updateMe', userData);
    },
    async profileTMX () {
      // Ensure sessionId exists before profiling
      if (!this.sessionId) {
        this.sessionId = uuidv4();
      }
      const profiling_domain = 'customer.homedepot.com';
      const org_id = this.$runtimeConstants.TMX_ORG_ID;
      const session_id = this.sessionId;
      const page_id = '10';
      await window.referral.profile(profiling_domain, org_id, session_id, page_id);
    },
    async updateTMX () {
      const body = {
        email: this.email,
        phone: this.phone,
        firstName: this.firstName,
        lastName: this.lastName,
        pageId: '10',
        sessionId: this.sessionId,
      };
      try {
        await axios.post('/proTools/update', body);
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@homeservices/design-kit/tools.scss";
.OPTINSECTION{
    border: 1px solid $color-gray30;
}
</style>
